"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  InfoTooltip,
  Refresh2,
  SimpleTooltipContent,
  Tooltip,
  useMediaQuery,
} from "@panda0/panda0-ui";
import { cn, HIREPANDA_LOGO, HIREPANDA_WORDMARK } from "@panda0/panda0-utils";
import { AnimatePresence, motion } from "framer-motion";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Brain,
  Clock,
  Download,
  FileText,
  MessageSquare,
  Plus,
  RefreshCcw,
  Star,
  ThumbsDown,
  ThumbsUp,
  Trash,
  Wand2,
} from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import ReactTextareaAutosize from "react-textarea-autosize";
import { toast } from "sonner";
import { z } from "zod";

import {
  JobFormData,
} from "@/lib/types";
import QuestionCard from "./quiz/question-card";
import { JobModalContext, SkillProgress } from "./types";
import DownloadPdf from "./quiz/download-pdf";

interface GenerationTask {
  taskId?: string;
  skillProgress?: Array<{
    skill: string;
    status: "pending" | "processing" | "completed" | "error";
    progress: number;
  }>;
}

function JobQuiz() {
  const { isMobile } = useMediaQuery();
  const { savedJobId, jobData, stateMachine, workspaceId } =
    useContext(JobModalContext);

  const [reactions, setReactions] = useState<{ [key: number]: string | null }>(
    {}
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationTaskId, setGenerationTaskId] =
    useState<GenerationTask | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState("");
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [questions, setQuestions] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  const [showInstructionsInput, setShowInstructionsInput] = useState(false);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);
  const [skillProgress, setSkillProgress] = useState<SkillProgress[]>([]);

  const {
    register,
    control,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<JobFormData>();

  // Load existing questions when component mounts
  useEffect(() => {
    if (jobData?.pandaQuiz?.questions) {
      setQuestions(jobData.pandaQuiz.questions);
    }
  }, [jobData]);

  const saveQuiz = async (updatedQuestions) => {
    if (!savedJobId) return;

    stateMachine.trackEvent("job_quiz_save", {
      question_count: updatedQuestions.length,
      question_types: updatedQuestions.map((q) => q.type || "standard"),
    });

    setIsSaving(true);
    try {
      setValue("pandaQuiz.questions", updatedQuestions, { shouldDirty: true });
      await stateMachine.saveStep("quiz", true);
      toast.success("Quiz saved successfully");
    } catch (error) {
      console.error("Error saving quiz:", error);
      toast.error("Failed to save quiz changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReactionClick = useCallback(
    (questionIndex: number, reaction: string) => {
      stateMachine.trackEvent("job_quiz_question_reaction", {
        question_index: questionIndex,
        reaction: reaction,
        previous_reaction: reactions[questionIndex],
      });

      setReactions((prev) => ({
        ...prev,
        [questionIndex]: prev[questionIndex] === reaction ? null : reaction,
      }));
    },
    [reactions, stateMachine]
  );

  const handleRemoveQuestion = useCallback(
    (index: number) => {
      stateMachine.trackEvent("job_quiz_remove_question", {
        question_index: index,
        question_text: questions[index]?.question?.substring(0, 50),
        total_questions: questions.length,
      });

      const newQuestions = [...questions];
      newQuestions.splice(index, 1);
      setQuestions(newQuestions);

      // Save changes to server
      saveQuiz(newQuestions);
    },
    [questions, saveQuiz, stateMachine]
  );

  const renderGenerationProgress = () => {
    if (!isGenerating || !generationTaskId) return null;

    // Filter to only show messages for skill generation
    const skillProgressMessages = generationTaskId?.skillProgress || [];
    const activeSkills = skillProgressMessages.filter(
      (skill) => skill.status !== "pending"
    );
    const segmentCount = Math.max(4, activeSkills.length || 1);

    return (
      <motion.div
        className="mb-6 flex justify-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
        layout
      >
        <div className="w-full max-w-2xl rounded-lg border border-green-100 bg-green-50 p-6">
          <motion.div className="flex items-center justify-between mb-3" layout>
            <h3 className="font-medium text-green-800">Generating Your Quiz</h3>
          </motion.div>

          {/* Per-skill progress blocks */}
          <AnimatePresence>
            {skillProgressMessages.length > 0 ? (
              <motion.div className="space-y-2 mt-4" layout>
                {skillProgressMessages.map((skillProgress, index) => {
                  // Only show skills that are being processed or completed
                  if (skillProgress.status === "pending") return null;

                  return (
                    <motion.div
                      key={skillProgress.skill}
                      className="relative overflow-hidden"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      layout
                    >
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-medium text-green-900">
                          {skillProgress.skill}
                        </span>
                        <span className="text-xs font-medium text-green-900">
                          {skillProgress.status === "completed"
                            ? "✓"
                            : skillProgress.status === "error"
                              ? "✗"
                              : `${skillProgress.progress}%`}
                        </span>
                      </div>

                      {/* Mini bamboo progress bar for each skill */}
                      <div className="h-2 w-full bg-green-200 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full rounded-full ${skillProgress.status === "error"
                            ? "bg-red-500"
                            : "bg-green-600"
                            }`}
                          initial={{ width: "0%" }}
                          animate={{ width: `${skillProgress.progress}%` }}
                          transition={{
                            duration: 0.5,
                            ease: "easeOut",
                          }}
                        ></motion.div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : null}
          </AnimatePresence>

          <motion.p
            className="mt-3 text-sm text-green-700 text-center"
            animate={{
              opacity: [0.7, 1, 0.7],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          >
            {generationMessage}
          </motion.p>
        </div>
      </motion.div>
    );
  };

  const downloadPDF = async () => {
    await DownloadPdf(questions, jobData?.basic?.title);
  }

  const generateQuiz = async () => {
    if (!savedJobId) {
      toast.error("Please save the job first");
      return;
    }

    stateMachine.trackEvent("job_quiz_generation_start", {
      has_skills: Boolean((jobData?.skills?.technicalSkills?.length ?? 0) > 0),
      skill_count: jobData?.skills?.technicalSkills?.length || 0,
      has_custom_instructions: Boolean(
        customInstructions && customInstructions.length > 0
      ),
    });

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationMessage("Starting generation...");

    try {
      const res = await fetch(
        `/api/v1/jobs/${savedJobId}/pandaquiz?workspaceId=${workspaceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customInstructions: customInstructions || undefined,
          }),
        }
      );

      if (!res.ok) {
        stateMachine.trackEvent("job_quiz_generation_api_error", {
          status: res.status,
          status_text: res.statusText,
        });
        throw new Error("Failed to start quiz generation");
      }

      const data = await res.json();

      if (data.status === "processing" && data.taskId) {
        stateMachine.trackEvent("job_quiz_generation_task_created", {
          task_id: data.taskId,
        });

        setGenerationTaskId(data.taskId);
        startPolling(data.taskId);
        toast.success("Quiz generation started!");
      } else {
        // Handle immediate response case
        if (data.questions) {
          stateMachine.trackEvent("job_quiz_generation_immediate", {
            question_count: data.questions.length,
          });

          setQuestions(data.questions);
          setIsGenerating(false);
          toast.success("Quiz generated successfully!");
        }
      }
    } catch (error) {
      stateMachine.trackEvent("job_quiz_generation_error", {
        error_message: error instanceof Error ? error.message : "Unknown error",
      });

      console.error("Error starting quiz generation:", error);
      setIsGenerating(false);
      toast.error("Failed to generate quiz");
    }
  };

  // Function to start polling for status updates
  const startPolling = useCallback(
    (taskId: string) => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }

      const interval = setInterval(async () => {
        try {
          const response = await fetch(
            `/api/v1/jobs/${savedJobId}/pandaquiz?workspaceId=${workspaceId}&taskId=${taskId}`
          );

          if (!response.ok) {
            console.error("Failed to fetch task status");
            return;
          }

          const data = await response.json();

          setGenerationProgress(data.progress || 0);
          setGenerationMessage(data.message || "Processing...");

          // Update skill progress if available
          if (data.skillProgress) {
            setGenerationTaskId((prev) => ({
              ...prev,
              skillProgress: data.skillProgress,
            }));
          }

          // If we have questions and the status is completed, update the UI
          if (data.status === "completed" || data.questions?.length > 0) {
            setQuestions(data.questions);
            setIsGenerating(false);
            setGenerationTaskId(null);
            clearInterval(interval);
            setPollingInterval(null);
            toast.success("Quiz generated successfully!");
          }

          // Handle error status
          if (data.status === "error") {
            setIsGenerating(false);
            setGenerationTaskId(null);
            clearInterval(interval);
            setPollingInterval(null);
            toast.error(data.message || "Failed to generate quiz");
          }
        } catch (error) {
          console.error("Error polling for task status:", error);
        }
      }, 3000); // Poll every 3 seconds for more responsive UI

      setPollingInterval(interval);
    },
    [savedJobId, workspaceId]
  );

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);


  // Handle enter key in instructions textarea
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      generateQuiz();
    }
  };

  // Focus input when it becomes visible
  useEffect(() => {
    if (showInstructionsInput && instructionsRef.current) {
      instructionsRef.current.focus();
    }
  }, [showInstructionsInput]);

  return (
    <motion.div
      className="mx-auto w-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Quiz header with actions */}
      <motion.div
        className="mb-6 flex flex-wrap items-center justify-between gap-3"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center gap-2">
          <motion.h2
            className="text-xl font-semibold"
            initial={{ x: -10 }}
            animate={{ x: 0 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            Panda Quiz
          </motion.h2>
          <InfoTooltip
            content={
              <SimpleTooltipContent
                title="This quiz is generated based on the skills you've identified. You can customize questions, regenerate, or download the quiz as PDF."
                cta="Learn more about PandaQuiz"
                href="#"
              />
            }
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button
              variant="primary"
              text={isMobile ? undefined : "Download PDF"}
              icon={<Download className="h-4 w-4" />}
              onClick={downloadPDF}
              disabled={questions.length === 0}
              className="h-9 w-fit"
            />
          </motion.div>

          {/* <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              variant="primary"
              text={isMobile ? undefined : "Regenerate"}
              icon={<Refresh2 className="h-4 w-4" />}
              className="h-9 w-fit"
              onClick={() => {
                toast.info("Custom question creation is coming soon!");
              }}
            />
          </motion.div> */}

          {/* <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              variant="outline"
              text={
                showInstructionsInput
                  ? "Hide Instructions"
                  : "Custom Instructions"
              }
              icon={<MessageSquare className="h-4 w-4" />}
              className="h-9 w-fit"
              onClick={() => setShowInstructionsInput(!showInstructionsInput)}
            />
          </motion.div> */}
        </div>
      </motion.div>

      {/* Custom Instructions Input */}
      <AnimatePresence>
        {showInstructionsInput && (
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col space-y-2 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-sm text-neutral-600">
                Pass instructions to fine-tune the question generation.
                HirePanda will try to adjust the questions generated
                accordingly.
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 overflow-hidden rounded-md border bg-white">
                  <ReactTextareaAutosize
                    ref={instructionsRef}
                    placeholder="Example: More system design questions, focus on React hooks"
                    className="w-full resize-none border-0 bg-transparent p-2 text-sm ring-0 focus:ring-0"
                    value={customInstructions}
                    onChange={(e) => {
                      // Limit to 100 characters
                      if (e.target.value.length <= 100) {
                        setCustomInstructions(e.target.value);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    maxLength={100}
                  />
                </div>
                <motion.div whileTap={{ scale: 0.95 }}>
                  <Button
                    className="h-9 w-fit shrink-0"
                    variant="primary"
                    onClick={generateQuiz}
                    loading={isGenerating}
                    disabled={isGenerating}
                    text="Generate"
                  />
                </motion.div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-neutral-500">
                  Press Enter to generate
                </span>
                <span className="text-xs text-neutral-500">
                  {customInstructions.length}/100
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {renderGenerationProgress()}

      {/* No questions state */}
      <AnimatePresence>
        {questions.length === 0 && !isGenerating ? (
          <motion.div
            className="flex flex-col items-center justify-center rounded-xl border border-neutral-200 bg-white py-16 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
          >
            <motion.div
              animate={{
                rotate: [0, 5, -5, 5, 0],
              }}
              transition={{
                duration: 2,
                ease: "easeInOut",
                repeat: Infinity,
                repeatDelay: 3,
              }}
            >
              <Wand2 className="mb-4 h-10 w-10 text-neutral-400" />
            </motion.div>
            <h3 className="mb-2 text-lg font-medium">
              No questions generated yet
            </h3>
            <p className="mb-6 max-w-md text-neutral-500">
              Generate questions based on the skills you've identified, or
              create custom questions manually.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                className="h-9 w-fit"
                variant="primary"
                text="Generate Questions"
                onClick={generateQuiz}
                loading={isGenerating}
                disabled={isGenerating || !savedJobId}
              />
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Questions list with progressive loading */}
      <motion.div id="quiz-content" className="space-y-6" layout>
        <AnimatePresence>
          {!isGenerating &&
            questions.map((question, questionIndex) => (
              <QuestionCard
                key={questionIndex}
                question={question}
                questionIndex={questionIndex}
                reactions={reactions}
                handleReactionClick={handleReactionClick}
                handleRemoveQuestion={handleRemoveQuestion}
                isMobile={isMobile}
              />
            ))}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

export default React.memo(JobQuiz);
